import { useCallback, useState } from 'react'

import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import useDraft from '@shared/hooks/src/useDraft'
import usePromiseLoading from '@shared/hooks/src/usePromiseLoading'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'

import TimelineNotes from '@components/TimelineNotes'

import { usePatientNoteCreation, usePatientNotes, useTaskNoteCreation, useTaskNotes } from './Notes.hooks'

export default function Notes({ task, disabled, includePatientNotes = false, inputSx = {}, containerRef }) {
  const pharmacyTaskKinds = useLookup(Lookup.PharmacyTasks, (data) => data.map(({ kind }) => kind))

  const [tab, setTab] = useState('task')
  const [createTaskComment, setCreateTaskComment] = useState(false)
  const { draft, setDraft, removeDraft } = useDraft(`task-${task.id}-comment`, { message: '', both: false })

  const disablePatientNotes = Boolean(task.escalationReason)

  const createPatientNote = usePatientNoteCreation(task.patientId)
  const createTaskNote = useTaskNoteCreation(task.id)

  const reset = useCallback(() => {
    removeDraft()
    setCreateTaskComment(false)
  }, [removeDraft])

  const [handleSubmit, isSubmitting] = usePromiseLoading((values) => {
    if (!values.message.trim()) return Promise.resolve()

    return Promise.all([
      ...(values.both || tab === 'task' ? [createTaskNote.mutateAsync({ message: values.message, createTaskComment })] : []),
      ...((values.both || tab === 'patient') && !disablePatientNotes ? [createPatientNote.mutateAsync(values.message)] : []),
    ]).then(reset)
  })

  const isPatientTab = tab === 'patient'
  const composerDisabled = disablePatientNotes && isPatientTab
  const showPharmacyTaskCheckbox = pharmacyTaskKinds.includes(task?.kind) && (tab === 'task' || draft?.both)

  return (
    <>
      <Stack>
        <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ pl: 1.5 }}>
          <Tab label="Task Comments" value="task" />
          {includePatientNotes && <Tab label="Patient Chart Notes" value="patient" />}
        </Tabs>

        {isPatientTab ? (
          <PatientNotes patientId={task.patientId} readOnly={disabled} />
        ) : (
          <TaskNotes taskId={task.id} readOnly={disabled} />
        )}
      </Stack>

      {disabled ? (
        <div />
      ) : (
        <Stack sx={[styles.sticky, inputSx]}>
          <Box sx={styles.composer}>
            <InputBase
              name="message"
              fullWidth
              multiline
              minRows={2}
              maxRows={8}
              onFocus={() => (containerRef ? containerRef.current.scrollIntoView({ block: 'end' }) : undefined)}
              placeholder={
                composerDisabled
                  ? 'Adding patient chart notes is disabled'
                  : includePatientNotes && isPatientTab
                    ? 'Add note to patient chart...'
                    : 'Add comment to task...'
              }
              disabled={isSubmitting || composerDisabled}
              value={draft?.message ?? ''}
              onChange={(e) => setDraft({ message: e.target.value, both: draft.both })}
              sx={styles.input}
            />
            <Stack direction="row" spacing={1} sx={styles.actions}>
              <Stack>
                {includePatientNotes && (
                  <FormControlLabel
                    name="both"
                    label={isPatientTab ? 'Include in task comments' : 'Include in patient chart notes'}
                    disabled={isSubmitting || disablePatientNotes}
                    control={
                      <Checkbox size="small" checked={draft?.both} onChange={(e, both) => setDraft({ message: draft.message, both })} />
                    }
                    labelPlacement="end"
                  />
                )}
                {showPharmacyTaskCheckbox && (
                  <FormControlLabel
                    name="createTaskComment"
                    label="Create pharmacy task from this note"
                    disabled={isSubmitting}
                    control={<Checkbox size="small" checked={createTaskComment} onChange={(e, checked) => setCreateTaskComment(checked)} />}
                    labelPlacement="end"
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" color="secondary" onClick={reset} disabled={isSubmitting || composerDisabled || !draft?.message}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  loading={isSubmitting}
                  variant="contained"
                  disabled={composerDisabled || !draft?.message?.trim()}
                  onClick={() => handleSubmit(draft)}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      )}
    </>
  )
}

function TaskNotes({ taskId, readOnly }) {
  const query = useTaskNotes(taskId)
  return (
    <Paper variant="outlined">
      <NotesTimeline query={query} readOnly={readOnly} />
    </Paper>
  )
}

function PatientNotes({ patientId, readOnly }) {
  const query = usePatientNotes(patientId)
  return (
    <Paper variant="outlined" sx={{ backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2) }}>
      <NotesTimeline query={query} readOnly={readOnly} showSource />
    </Paper>
  )
}

function NotesTimeline({ query, readOnly, showSource = false }) {
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = query

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Stack sx={{ px: 2 }}>
      {showLoading && <TimelineNotes.Loading showSource={showSource} />}
      {showEmpty && <TimelineNotes.Empty readOnly={readOnly} />}
      {showData && (
        <TimelineNotes>
          {data.map((note, i) => (
            <TimelineNotes.Item key={note.id} data={note} showSource={showSource} isLast={data.length - 1 === i} />
          ))}
          {hasNextPage && (
            <Button
              fullWidth
              loading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              endIcon={<MoreOutlinedIcon rotate={90} />}
              loadingPosition="end"
              sx={{ textTransform: 'none' }}
            >
              more
            </Button>
          )}
        </TimelineNotes>
      )}
    </Stack>
  )
}

const styles = {
  sticky: {
    zIndex: 1,
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'background.paper',
    mx: (theme) => `-${theme.spacing(3)} !important`,
    borderTop: '1px solid',
    borderColor: 'divider',
    p: 2,
  },
  composer: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    transition: 'border-color 150ms ease-in-out',
    '&:focus-within': {
      borderColor: 'primary.main',
    },
  },
  input: {
    px: 1.5,
    py: 1,
  },
  actions: {
    px: 1,
    pb: 0.5,
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
}
